import { Address } from '@website/types/graphqlOperations';

/**
 * Concatenate strings and remove duplicates
 */
function concat(...items: string[]): string | undefined {
  const itemsWithoutDuplicates = Array.from(new Set(items)); // Remove duplicates
  const res = itemsWithoutDuplicates
    .filter((i) => i && i !== ' ')
    .map((i) => i.trim())
    .join(', ');
  if (res === '') {
    return undefined;
  }
  return res;
}

/**
 * Convert google address object to our standard one
 */
export default function googleAddressToStandard(
  values: google.maps.GeocoderResult,
  placeName: string,
): Address {
  // Flatten object from google places
  const flattenedAddressComponent = new Map();

  values.address_components.forEach((item) => {
    item.types.forEach((type) => {
      if (type === 'country') {
        flattenedAddressComponent.set(type, item.short_name);
        return;
      }
      flattenedAddressComponent.set(type, item.long_name);
    });
  });

  const streetAddress = flattenedAddressComponent.get('route')
    ? `${flattenedAddressComponent.get('street_number') || ''} ${
        flattenedAddressComponent.get('route') || ''
      }`
    : flattenedAddressComponent.get('street_address');

  const neighborhood = concat(
    flattenedAddressComponent.get('intersection'),
    flattenedAddressComponent.get('neighborhood'),
    flattenedAddressComponent.get('sublocality'),
    flattenedAddressComponent.get('sublocality_level_1'),
    flattenedAddressComponent.get('sublocality_level_2'),
    flattenedAddressComponent.get('sublocality_level_3'),
  );

  return {
    addressLine1: placeName,
    addressLine2: !streetAddress?.includes(placeName)
      ? streetAddress || neighborhood
      : neighborhood,
    locality: concat(
      flattenedAddressComponent.get('locality'),
      flattenedAddressComponent.get('postal_town'),
    ),
    dependentLocality: concat(
      flattenedAddressComponent.get('administrative_area_level_5'),
      flattenedAddressComponent.get('administrative_area_level_4'),
      flattenedAddressComponent.get('administrative_area_level_3'),
    ),
    administrativeArea: concat(
      flattenedAddressComponent.get('administrative_area_level_2'),
      flattenedAddressComponent.get('administrative_area_level_1'),
    ),
    postalCode: flattenedAddressComponent.get('postal_code'),
    country: flattenedAddressComponent.get('country'),
    coordinates: {
      lat: values.geometry.location.lat(),
      lng: values.geometry.location.lng(),
    },
  };
}
